import classNames from 'classnames';
import {Provider} from 'mobx-react';
import {useState} from 'react';

import {AsyncPriceCourseCardContext, BrowseCourseBadgesContext} from '@udemy/browse-course';
import {FunnelLogContextProvider, FunnelLogContextProviderProps} from '@udemy/funnel-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {
    CourseUnitSkeletonVerticalLayout,
    DiscoveryUnitsContainerStore,
    ProfessionalSkillsUnit as PSU,
    ProfessionalSkillsUnitProps as PSUProps,
    CourseDetailsQuickViewBoxContext,
} from '@udemy/react-discovery-units';
import {CourseCardSkeletonGroup} from '@udemy/react-reveal-components';
import {pxToRem} from '@udemy/styles';
import {useUDData} from '@udemy/ud-data';

import {
    browseCourseContextProps,
    minimalBrowseCourseContextProps,
} from '../../lib/browseContextPropsConfig';
import styles from './professional-skills-showcase.module.less';

export interface ProfessionalSkillsUnitProps {
    pageType: FunnelLogContextProviderProps['pageType'];
    contentPanelClassName?: string;
    loadingSkeleton?: React.ReactNode;
    isVerticalRecoUnitsFeatureEnabled?: boolean;
    showAddToCartButtonsInVerticalLayout?: boolean;
    /** TODO: clean up once lohp_carousel_hover_popup_removal experiment concludes */
    showStateAwareCta?: boolean;
}

/**
 * @remarks A simple temporary skeleton for the PSU component. NEEDS TO BE REMOVED AFTER THE PSU REORDERING EXPERIMENT DONE
 * TODO: Please remove this after PSU re-ordering Experiment - https://udemyjira.atlassian.net/browse/TB-10066
 */
export const PSUSkeleton = ({withTitle = true}: {withTitle?: boolean}) => {
    const CustomCardWrapper = ({children}: {children: React.ReactNode}) => (
        <div
            style={{
                inlineSize: `${pxToRem(300)}rem`,
            }}
        >
            {children}
        </div>
    );
    return (
        <CourseCardSkeletonGroup
            withTitle={withTitle}
            cardCountPerRow={4}
            cardWrapper={CustomCardWrapper}
        />
    );
};

export const ProfessionalSkillsUnit = ({
    pageType,
    contentPanelClassName = '',
    unitData,
    loadingSkeleton = null,
    isVerticalRecoUnitsFeatureEnabled,
    showAddToCartButtonsInVerticalLayout,
    showStateAwareCta = false,
}: ProfessionalSkillsUnitProps & Pick<PSUProps, 'unitData'>) => {
    const udData = useUDData();
    const [discoveryUnitsContainerStore] = useState(
        () => new DiscoveryUnitsContainerStore({pageType}, udData),
    );

    const isMobileMax = useMatchMedia('mobile-max');

    const showVerticalRecoUnits = isVerticalRecoUnitsFeatureEnabled && !!isMobileMax;

    return (
        <FunnelLogContextProvider pageType={pageType}>
            <CourseDetailsQuickViewBoxContext.Provider value={{showAiGeneratedHighlights: true}}>
                <Provider discoveryUnitsStore={discoveryUnitsContainerStore}>
                    {loadingSkeleton ?? (
                        <BrowseCourseBadgesContext.Provider
                            value={
                                showVerticalRecoUnits
                                    ? browseCourseContextProps
                                    : minimalBrowseCourseContextProps
                            }
                        >
                            <AsyncPriceCourseCardContext.Provider
                                value={{
                                    useCourseProductCard: true,
                                    hideCourseDescription: true,
                                }}
                            >
                                <PSU
                                    className={styles['professional-skills-unit']}
                                    contentPanelClassName={classNames(
                                        styles['panel-content'],
                                        contentPanelClassName,
                                    )}
                                    unitData={unitData}
                                    contentLoadingSkeleton={
                                        showVerticalRecoUnits ? (
                                            <CourseUnitSkeletonVerticalLayout />
                                        ) : (
                                            <PSUSkeleton withTitle={false} />
                                        )
                                    }
                                    showStackedProductCardLayout={showVerticalRecoUnits}
                                    showAddToCartInStackedProductCardLayout={
                                        showAddToCartButtonsInVerticalLayout
                                    }
                                    showStateAwareCtaInCourseCard={showStateAwareCta}
                                    showSkillFilters={false}
                                    heading={gettext('Skills to transform your career and life')}
                                    showAllButtonStyle="ghost"
                                    headingClassName="ud-heading-xxl"
                                    courseCardProps={{
                                        ribbonHighlighted: false,
                                        showCourseTag: false,
                                    }}
                                    showBackgroundColor={false}
                                />
                            </AsyncPriceCourseCardContext.Provider>
                        </BrowseCourseBadgesContext.Provider>
                    )}
                </Provider>
            </CourseDetailsQuickViewBoxContext.Provider>
        </FunnelLogContextProvider>
    );
};
